/**
 * Composable для обработки изображений с оптимизацией качества
 */

export interface CropArea {
    x: number
    y: number
    width: number
    height: number
}

export function useImageProcessor() {
    /**
     * Определить оптимальное качество JPEG на основе размера изображения
     * Большие изображения -> меньше качество для оптимизации размера файла
     * Маленькие изображения -> больше качество для сохранения деталей
     */
    const getOptimalQuality = (width: number, height: number): number => {
        const pixels = width * height

        // 4K+ (8+ мегапикселей) -> качество 0.80
        if (pixels >= 8_000_000) return 0.8

        // 1080p-4K (2-8 мегапикселей) -> качество 0.85
        if (pixels >= 2_000_000) return 0.85

        // 720p-1080p (0.9-2 мегапикселей) -> качество 0.88
        if (pixels >= 900_000) return 0.88

        // Меньше 720p -> качество 0.92
        return 0.92
    }

    /**
     * Рассчитать область кропа на основе координат ретикла и видео
     * Учитывает object-fit: cover и масштабирование
     * Добавляет отступ вокруг ретикла
     */
    const calculateCropArea = (
        reticleBounds: DOMRect,
        videoBounds: DOMRect,
        sourceWidth: number,
        sourceHeight: number
    ): CropArea => {
        const containerW = videoBounds.width
        const containerH = videoBounds.height

        // object-fit: cover → масштаб и смещения видимой области источника
        const scale = Math.max(containerW / sourceWidth, containerH / sourceHeight)
        const displayW = sourceWidth * scale
        const displayH = sourceHeight * scale
        const offsetX = (displayW - containerW) / 2
        const offsetY = (displayH - containerH) / 2

        const retLeftCSS = reticleBounds.left - videoBounds.left
        const retTopCSS = reticleBounds.top - videoBounds.top
        const retWidthCSS = reticleBounds.width
        const retHeightCSS = reticleBounds.height

        // Добавляем отступ со всех сторон в CSS координатах
        const paddingCSS = 70
        const expandedLeftCSS = retLeftCSS - paddingCSS
        const expandedTopCSS = retTopCSS - paddingCSS
        const expandedWidthCSS = retWidthCSS + paddingCSS * 2 // слева + справа
        const expandedHeightCSS = retHeightCSS + paddingCSS * 2 // сверху + снизу

        // Переводим в координаты исходного изображения с учетом масштаба
        let cropX = (expandedLeftCSS + offsetX) / scale
        let cropY = (expandedTopCSS + offsetY) / scale
        let cropW = expandedWidthCSS / scale
        let cropH = expandedHeightCSS / scale

        // Ограничим в пределах исходного кадра
        cropX = Math.max(0, Math.min(cropX, sourceWidth))
        cropY = Math.max(0, Math.min(cropY, sourceHeight))
        if (cropX + cropW > sourceWidth) cropW = sourceWidth - cropX
        if (cropY + cropH > sourceHeight) cropH = sourceHeight - cropY

        return {
            x: cropX,
            y: cropY,
            width: cropW,
            height: cropH,
        }
    }

    /**
     * Захватить и обрезать изображение из видео элемента
     */
    const captureAndCrop = async (
        videoElement: HTMLVideoElement,
        reticleElement: HTMLDivElement
    ): Promise<{ file: File; blob: Blob; url: string }> => {
        // 1. Захват полного кадра
        const fullCanvas = document.createElement('canvas')
        fullCanvas.width = videoElement.videoWidth
        fullCanvas.height = videoElement.videoHeight
        const fullCtx = fullCanvas.getContext('2d')

        if (!fullCtx) {
            throw new Error('Failed to get canvas context')
        }

        fullCtx.drawImage(videoElement, 0, 0, fullCanvas.width, fullCanvas.height)

        // 2. Рассчитать область кропа
        const reticleRect = reticleElement.getBoundingClientRect()
        const videoRect = videoElement.getBoundingClientRect()

        const cropArea = calculateCropArea(
            reticleRect,
            videoRect,
            fullCanvas.width,
            fullCanvas.height
        )

        // 3. Создать обрезанный canvas
        const cropCanvas = document.createElement('canvas')
        cropCanvas.width = Math.round(cropArea.width)
        cropCanvas.height = Math.round(cropArea.height)
        const cropCtx = cropCanvas.getContext('2d')

        if (!cropCtx) {
            throw new Error('Failed to get crop canvas context')
        }

        cropCtx.drawImage(
            fullCanvas,
            cropArea.x,
            cropArea.y,
            cropArea.width,
            cropArea.height,
            0,
            0,
            cropCanvas.width,
            cropCanvas.height
        )

        // 4. Определить оптимальное качество на основе размера
        const quality = getOptimalQuality(cropCanvas.width, cropCanvas.height)

        console.log(
            `Capturing image: ${cropCanvas.width}x${cropCanvas.height}, quality: ${quality}`
        )

        // 5. Конвертировать в blob и file
        const blob: Blob = await new Promise((resolve, reject) => {
            cropCanvas.toBlob(
                (b) => {
                    if (b) {
                        resolve(b)
                    } else {
                        reject(new Error('Failed to create blob'))
                    }
                },
                'image/jpeg',
                quality
            )
        })

        const fileName = `scan_${Date.now()}.jpg`
        const file = new File([blob], fileName, { type: 'image/jpeg' })
        const url = URL.createObjectURL(blob)

        console.log(`Image captured: ${file.name}, size: ${(file.size / 1024).toFixed(1)}KB`)

        return { file, blob, url }
    }

    /**
     * Очистить URL объекта для предотвращения утечек памяти
     */
    const revokeObjectURL = (url: string | null) => {
        if (url) {
            URL.revokeObjectURL(url)
        }
    }

    return {
        getOptimalQuality,
        calculateCropArea,
        captureAndCrop,
        revokeObjectURL,
    }
}
